/**
 * GraphTabs.tsx — the pinned tab strip for a project's graphs, plus the
 * transient REPLAY tabs.
 *
 * One tab per graph in the project (`graphs/<id>.json`). The `Main` graph
 * (id `main`) is always present and non-removable — it shows no delete
 * affordance. Other graphs can be renamed (pencil → PromptDialog) or removed
 * (trash → ConfirmDialog). There is NO inline close-X on any GRAPH tab —
 * graphs are pinned (stricter than dashboard tabs); removal is always a
 * deliberate trash → confirm action.
 *
 * Replay tabs (a recorded period opened read-only — src/replay/) are the
 * opposite lifecycle: TRANSIENT documents with an inline close-X (closing
 * one discards nothing; the recording stays in its dataset run). They
 * render after the graph tabs, prefixed with a clock glyph, and the
 * trailing clock button opens the ReplayPicker (the `+` keeps adding
 * graphs). Switching the active graph is a per-client UI choice (which
 * graph the editor canvas shows); it is not broadcast over the WS.
 * Per-graph edits carry the active id as their `graphId`. Mirrors the
 * `.dashboard-tabs` style.
 */
import { memo } from 'react';
import { Plus, PencilSimple, Trash, ClockCounterClockwise, X } from '@phosphor-icons/react';
import { MAIN_GRAPH_ID } from '../engine/types';

interface ReplayTabInfo {
  key: string;
  label: string;
  /** The underlying dataset run id (the tab title's provenance hint). */
  runId?: string;
}

interface GraphTabsProps {
  graphs: { id: string; name: string }[];
  activeId: string;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onRename: (id: string) => void;
  onDelete: (id: string) => void;
  /** Open replay tabs (read-only recorded periods), after the graph tabs. */
  replays: ReplayTabInfo[];
  /** The active replay tab's key — when set, a replay tab (not a graph) owns
   *  the canvas and the graph tabs render unactive. */
  activeReplayKey: string | null;
  onSelectReplay: (key: string) => void;
  onCloseReplay: (key: string) => void;
  /** Double-click a replay tab → rename it (the run's display name for
   *  run-derived tabs, the doc's label for saved periods). */
  onRenameReplay?: (key: string) => void;
  /** Open the ReplayPicker (browse recordings + saved periods). */
  onOpenReplayPicker: () => void;
}

function GraphTabsImpl({
  graphs,
  activeId,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  replays,
  activeReplayKey,
  onSelectReplay,
  onCloseReplay,
  onRenameReplay,
  onOpenReplayPicker,
}: GraphTabsProps): JSX.Element {
  const replayActive = activeReplayKey !== null;
  return (
    <div className="graph-tabs">
      {graphs.map((g) => {
        const isMain = g.id === MAIN_GRAPH_ID;
        const active = !replayActive && activeId === g.id;
        return (
          <div
            key={g.id}
            className={`graph-tab${active ? ' active' : ''}`}
            onClick={() => onSelect(g.id)}
            title={isMain ? 'Main graph (cannot be removed)' : g.name}
          >
            <span className="graph-tab-name">{g.name}</span>
            {active && (
              <span className="graph-tab-actions">
                <button
                  type="button"
                  className="btn-icon-mini"
                  title="Rename graph"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRename(g.id);
                  }}
                >
                  <PencilSimple size={11} />
                </button>
                {/* Main is non-removable — no delete affordance. Removal of
                 * other graphs is a deliberate trash → ConfirmDialog (never a
                 * quick close-X), honoring the pinned-tab UX. */}
                {!isMain && (
                  <button
                    type="button"
                    className="btn-icon-mini"
                    title="Delete graph"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(g.id);
                    }}
                  >
                    <Trash size={11} />
                  </button>
                )}
              </span>
            )}
          </div>
        );
      })}
      {replays.map((r) => (
        <div
          key={r.key}
          className={`graph-tab graph-tab-replay${activeReplayKey === r.key ? ' active' : ''}`}
          onClick={() => onSelectReplay(r.key)}
          onDoubleClick={() => onRenameReplay?.(r.key)}
          title={`Replay — ${r.label}${r.runId ? ` (${r.runId})` : ''} · double-click to rename`}
        >
          <ClockCounterClockwise size={11} />
          <span className="graph-tab-name">{r.label}</span>
          {/* Transient document — a plain close-X, no confirm (the recording
           * itself stays in its dataset run; only the tab goes away). */}
          <button
            type="button"
            className="btn-icon-mini graph-tab-close"
            title="Close replay tab"
            onClick={(e) => {
              e.stopPropagation();
              onCloseReplay(r.key);
            }}
          >
            <X size={11} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="graph-tab graph-tab-add"
        title="New graph"
        onClick={onCreate}
      >
        <Plus size={14} />
      </button>
      <button
        type="button"
        className="graph-tab graph-tab-add"
        title="Open a replay (browse recordings)"
        onClick={onOpenReplayPicker}
      >
        <ClockCounterClockwise size={14} />
      </button>
    </div>
  );
}

export const GraphTabs = memo(GraphTabsImpl);
